import { Modal } from 'antd';
import config from 'config';
import { FormikProvider, setNestedObjectValues, useFormik } from 'formik';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import {
    TemplateResponse,
    Template,
    TemplateContext,
    TemplateFormType,
    useTemplateContext,
} from '../../components/ui/composed/template/TemplateContext';
import axiosClient from 'config/axios';
import { SuccessResponse } from 'core/http/response';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import parseTemplateOptions from '../../components/ui/composed/template/parseTemplateOptions';
import TemplateHeader from '../../components/ui/composed/template/TemplateHeader';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { clone, cloneDeep, isEmpty, isEqual, set, setWith } from 'lodash';
import { createTemplate, editTemplate } from 'store/template/client';
import validate, {
    FormModel,
    injectTemplateValidations,
    transformErrorsForFormik,
} from 'views/declarations/uk/export/validations/validations';
import * as Yup from 'yup';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import ValidationErrorContainer from 'views/declarations/common/ValidationErrorContainer/ValidationErrorContainer';
import useGetDeclarationMapValues from '../../hooks/useGetDeclarationMapValues';
import useCodelists from 'hooks/useCodelists';
import { DeclarationTypes, ICS2MessageTypes, MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { getDeclarationTypeByTemplate, getTransformedCountry } from 'views/declarations/utils/declaration-utils';
import useSession from 'hooks/useSession';
import { useMatch, useParams } from 'react-router-dom';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { StyledHeader, StyledLayout } from 'views/custom-declaration/products/Products.styles';
import DeclarationFormContent from 'views/declarations/declaration-tabs/declaration-form/DeclarationFormContent';
import MasterDetails from 'views/declarations/declaration-tabs/declaration-form/tabs/master/MasterDetails';
import { DeclarationContextProvider } from 'views/declarations/common/DeclarationContext';
import Product from 'views/declarations/declaration-tabs/declaration-form/tabs/products/Product';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import { buildIcs2TemplateFormErrors, transformTemplateData } from './utils';
import ICS2ENSHouseConsignmentForm from 'views/declarations/eu/ics2/ICS2ENSHouseConsignmentForm';
import { getIcs2EnsTemplate, updateIcs2EnsTemplate } from 'store/template/ics2/client';
import { fromIcs2EnsTemplate, toIcs2EnsTemplate } from 'store/template/ics2/ics2EnsTemplateMapper';
import {
    getIcs2EnsTemplateFormOptions,
    getIcs2EnsTemplateLevels,
} from 'views/declarations/eu/ics2/ics2-ens-template-levels';

const TemplateEdit = (): ReactElement => {
    const creating = useMatch('/templates/add');
    const {
        closeModal,
        templateId: contextTemplateId,
        options,
        setForm,
        form,
        isViewOnly,
        openModal,
    } = useTemplateContext();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { setFormDeclarationErrors } = useDeclarationFormErrors();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { templateId: paramsTemplateId } = useParams();
    const { getCodeLists } = useCodelists();
    const { customerId } = useSession();

    const [activeForm, setActiveForm] = useState<TemplateFormType>('master');
    const [template, setTemplate] = useState<TemplateResponse | undefined>(undefined);
    const [saveChanges, setSaveChanges] = useState<boolean>(false);
    const [editedTemplateName, setEditedTemplateName] = useState<string | null | undefined>(undefined);
    const [errorsModalOpen, setErrorsModalOpen] = useState(false);

    const templateId = contextTemplateId || paramsTemplateId;
    const isCds = useMemo(() => options?.country === 'CDS' || options?.country === 'UK', [options?.country]);
    const isIcs2 = useMemo(() => options?.country === 'EU', [options?.country]);
    // Which levels this ICS2 message type accepts. F50 carries no master goods item, so its
    // builder has no Master Item tab and its template must not be sent one.
    const ics2Levels = useMemo(
        () => (isIcs2 ? getIcs2EnsTemplateLevels(options?.formType) : []),
        [isIcs2, options?.formType]
    );
    const { path, declaration } = useMemo(() => parseTemplateOptions(options), [options]);

    const decViewMapOptions = useMemo(() => {
        const _template = template
            ? (setWith(clone(template), 'formType', options?.formType, clone) as any)
            : undefined;
        const _options = _template ?? {
            declarationInternalType: options?.declarationType,
            declarationExternalType:
                options?.country === 'IRELAND' ? 'REVENUE' : options?.country === 'EU' ? 'ICS2' : 'CDS',
            formType: options?.formType,
        };

        _options.declarationExternalEntity = _options.declarationExternalType;
        delete _options.declarationExternalType;
        if (!_options) return null;

        return _options;
    }, [options?.country, options?.declarationType, options?.formType, template]);

    const declarationViewMapValues = useGetDeclarationMapValues({ options: decViewMapOptions });

    const templateValidation = useMemo(() => {
        const master = injectTemplateValidations(declarationViewMapValues?.declarationFormValidations);
        const item = injectTemplateValidations(declarationViewMapValues?.productFormValidations);
        const houseConsignment = injectTemplateValidations(declarationViewMapValues?.houseConsignmentFormValidations);
        return { master, item, houseConsignment };
    }, [
        declarationViewMapValues?.declarationFormValidations,
        declarationViewMapValues?.productFormValidations,
        declarationViewMapValues?.houseConsignmentFormValidations,
    ]);

    const templateData = useMemo(() => {
        const data = cloneDeep(template?.template);
        if (isCds && data) {
            data.master.defaults = { cdsDeclarationPayload: data.master.defaults };
        }
        const transformedData = transformTemplateData(data, declarationViewMapValues?.transformData, 'forClient');
        if (isCds && transformedData) {
            transformedData.master.defaults = transformedData.master.defaults.cdsDeclarationPayload;
        }
        return transformedData;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template?.template]);

    const templateFormik = useFormik<Template>({
        initialValues: templateData ?? {
            master: { defaults: declarationViewMapValues?.structure ?? {}, meta: {} },
            product: { defaults: declarationViewMapValues?.itemStructure ?? {}, meta: {} },
            // Only declaration types with a house level (ICS2 ENS) use these branches, and only
            // for the levels the message type's topology allows.
            ...(ics2Levels.includes('houseConsignment')
                ? {
                      houseConsignment: {
                          defaults: declarationViewMapValues?.houseConsignmentStructure ?? {},
                          meta: {},
                      },
                  }
                : {}),
            ...(ics2Levels.includes('houseGoodsItem')
                ? {
                      houseGoodsItem: {
                          defaults: declarationViewMapValues?.houseGoodsItemStructure ?? {},
                          meta: {},
                      },
                  }
                : {}),
        },
        validateOnMount: true,
        validateOnChange: false,
        enableReinitialize: true,
        validationSchema:
            declarationViewMapValues?.declarationValidationSchema && declarationViewMapValues?.productValidationSchema
                ? Yup.object().shape({
                      master: Yup.object().shape({
                          defaults:
                              declarationViewMapValues.declarationValidationSchema instanceof Function
                                  ? declarationViewMapValues.declarationValidationSchema(1)
                                  : declarationViewMapValues.declarationValidationSchema,
                      }),
                      product: Yup.object().shape({
                          defaults: declarationViewMapValues.productValidationSchema,
                      }),
                  })
                : undefined,
        validate:
            declarationViewMapValues?.declarationFormValidations && declarationViewMapValues?.productFormValidations
                ? async (values) => {
                      // TODO: Memoize already validated fields to speed up the process.
                      // If we don't do this, we may end up with a lot of unnecessary requests to eori check service for example.
                      return transformErrorsForFormik(
                          await validate(new FormModel(values), {
                              childValidators: {
                                  'master.defaults': templateValidation.master,
                                  'product.defaults': templateValidation.item,
                                  ...(templateValidation.houseConsignment
                                      ? { 'houseConsignment.defaults': templateValidation.houseConsignment }
                                      : {}),
                                  // Master and house goods items share one item schema.
                                  ...(ics2Levels.includes('houseGoodsItem')
                                      ? { 'houseGoodsItem.defaults': templateValidation.item }
                                      : {}),
                              },
                          })
                      );
                  }
                : () => {},
        onSubmit: () => {},
    });

    const openErrorsModal = () => setErrorsModalOpen(true);
    const closeErrorsModal = () => setErrorsModalOpen(false);

    // This is duplicating
    const edit = async (data: Template | undefined, templateName?: string | null) => {
        const _template = template ?? ({} as TemplateResponse);
        try {
            if (isIcs2) {
                // `declarationName` is immutable on the backend, so it comes from the stored
                // template rather than from the builder's options.
                const declarationName = (_template.declarationName ?? options?.formType) as ICS2MessageTypes;
                // The loaded template is the better source, but the id the screen was opened with
                // is the one that is always known - falling through to `undefined` would send the
                // update to a url ending in "undefined".
                const id = _template.id ?? templateId;
                if (!id) throw new Error('Cannot update a template without an id');

                await updateIcs2EnsTemplate(id, {
                    declarationName,
                    templateName: templateName ?? 'Template',
                    template: toIcs2EnsTemplate(data, declarationName),
                    teamId: options?.teamId,
                });
            } else {
                await editTemplate({
                    ..._template,
                    templateName: templateName ?? 'Template',
                    template: data,
                });
            }
            Notification({
                type: 'success',
                messageTitle: 'Success',
                description: 'Successfully updated template',
                placement: 'top',
            });
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: errorMessage || DEFAULT_INTERNAL_ERROR_MESSAGE,
                placement: 'top',
            });
        }
    };

    const create = async (data: Template | undefined, templateName?: string | null) => {
        try {
            if (!options?.country || !options.declarationType || !options.formType || !data)
                throw new Error('Data missing for creating template');

            const country =
                options.country === 'CDS' || options.country === 'HMRC'
                    ? 'uk'
                    : options.country === 'REVENUE'
                    ? 'ireland'
                    : (options.country.toLowerCase() as 'ireland' | 'uk' | 'eu');

            await createTemplate(
                country,
                options.declarationType.toLowerCase() as 'import' | 'export' | 'ens' | 'ncts',
                options.formType!,
                {
                    declarationName: options.formType,
                    templateName: templateName ?? 'Template',
                    template: data,
                },
                options.teamId
            );

            Notification({
                type: 'success',
                messageTitle: 'Success',
                description: 'Successfully created template',
                placement: 'top',
            });
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: errorMessage || DEFAULT_INTERNAL_ERROR_MESSAGE,
                placement: 'top',
            });
        }
    };

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Templates',
                path: '/templates',
            },
            {
                breadcrumbName: 'Form Template Builder',
                path: '',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    useEffect(() => {
        const isTemplateUpdated = !isEqual(templateFormik.initialValues, templateFormik.values);

        setSaveChanges(isTemplateUpdated);
    }, [templateFormik.initialValues, templateFormik.values]);

    useEffect(() => {
        const transformedCountry = getTransformedCountry(options?.country);
        const declarationType = getDeclarationTypeByTemplate(options?.country, options?.declarationType);
        getCodeLists(transformedCountry, declarationType as DeclarationTypes, customerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options?.country, options?.declarationType, customerId]);

    useEffect(() => {
        if (templateId && !creating) {
            showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Loading template data...' } });
            axiosClient
                .get<SuccessResponse<TemplateResponse>>(`${config.declarationTemplatesUrl}/${templateId}`)
                .then(async (res) => {
                    const _template = res.data.payload;
                    if (!_template) return;

                    const extenalType = _template?.declarationExternalType;

                    if (!contextTemplateId)
                        openModal?.({
                            templateId: _template?.id,
                            country: (extenalType === 'REVENUE'
                                ? 'IRELAND'
                                : extenalType === 'ICS2'
                                ? 'EU'
                                : extenalType) as any,
                            declarationType: _template?.declarationInternalType as any,
                            formType: _template?.declarationName as any,
                            name: _template?.templateName,
                            viewOnly: false,
                        });

                    // The generic endpoint hands back the ICS2 template in the two-level pair it is
                    // stored as, which holds no house goods item and wraps every level's defaults.
                    // Read it again through the ICS2 endpoint, whose shape the mapper understands.
                    if (extenalType === 'ICS2') {
                        const ics2Template = await getIcs2EnsTemplate(_template.id);
                        setTemplate({ ..._template, template: fromIcs2EnsTemplate(ics2Template.template) });
                    } else {
                        setTemplate(_template);
                    }
                })
                // Without this the builder would sit on an unloaded template and save it as a new
                // one, or update nothing at all. Templates written before ICS2 had its own
                // endpoints are stored untyped and are refused here - they have to be recreated.
                .catch((err) => {
                    Notification({
                        type: 'error',
                        messageTitle: 'Error',
                        description: getErrorMessage(err) || 'Could not load the template.',
                        placement: 'top',
                    });
                })
                .finally(() => hideGlobalOverlay());
        } else if (creating && !options) {
            openModal?.({
                country: 'IRELAND',
                declarationType: DeclarationInternalType.IMPORT,
                formType: 'H1' as MessageTypes,
                name: 'Template',
                viewOnly: false,
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId]);

    const handleOkay = async (templateName?: string | null) => {
        try {
            const errors = (await templateFormik.validateForm()) as any;
            if (!isEmpty(errors)) {
                templateFormik.setTouched(setNestedObjectValues(errors, true));
                let newErrors = {
                    ...(errors.master?.defaults ?? {}),
                };
                // ICS2 spreads its levels across four keys and addresses its item levels through
                // `itemPaths`, so the single `itemPath` graft below never reaches them.
                if (isIcs2) {
                    newErrors = buildIcs2TemplateFormErrors(errors, declarationViewMapValues?.itemPaths);
                } else if (declarationViewMapValues?.itemPath)
                    newErrors = set(
                        newErrors,
                        declarationViewMapValues.itemPath,
                        errors.product?.defaults ? [errors.product.defaults] : []
                    );
                if (declarationViewMapValues?.parseForm) {
                    setFormDeclarationErrors(declarationViewMapValues.parseForm(newErrors));
                }
                openErrorsModal();
                return;
            }

            const values = cloneDeep(templateFormik.values);
            if (isCds) {
                values.master.defaults = {
                    cdsDeclarationPayload: templateFormik.values.master.defaults,
                };
            }
            const data = transformTemplateData(values, declarationViewMapValues?.transformData, 'forServer');
            if (isCds && data) {
                data.master.defaults = data.master.defaults.cdsDeclarationPayload;
            }
            if (!creating) {
                await edit(data, templateName);
            } else {
                await create(data, templateName);
            }

            // Closing returns to the dashboard, which reloads its list with the params that were
            // applied before the builder was opened (kept in TemplateView).
            closeModal?.();
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: errorMessage || DEFAULT_INTERNAL_ERROR_MESSAGE,
                placement: 'top',
            });
        }
    };

    const handleChangeForm = (form: TemplateFormType) => {
        setActiveForm(form);
        setForm?.(form);
    };

    const saveChangesModal = () =>
        Modal.confirm({
            content: `Do you want to save Template ${editedTemplateName ?? options?.name} ?`,
            closable: true,
            cancelText: 'No',
            okText: 'Yes',
            onOk: () => handleOkay(),
            onCancel: () => closeModal?.(),
        });

    return (
        <>
            <FormikProvider value={templateFormik}>
                <StyledHeader
                    style={{
                        height: 'auto',
                    }}
                >
                    <TemplateHeader
                        creating={!!creating}
                        formOptions={isIcs2 ? getIcs2EnsTemplateFormOptions(options?.formType) : undefined}
                        onSave={handleOkay}
                        onClose={saveChanges ? saveChangesModal : closeModal}
                        onChangeActiveForm={handleChangeForm}
                        active={activeForm}
                        setEditedTemplateName={(editedTemplateName: string | null) =>
                            setEditedTemplateName(editedTemplateName)
                        }
                    />
                </StyledHeader>

                <TemplateContext.Provider value={{ templateFormik, template: true, form, options, isViewOnly }}>
                    <DeclarationContextProvider>
                        <StyledLayout
                            style={{
                                height: '100%',
                            }}
                        >
                            <DeclarationFormContent>
                                {activeForm === 'master' && <MasterDetails declaration={declaration} />}
                                {/* Both goods-item levels use the same form — the level it writes
                                    to comes from TemplateContext.form via useTemplatePath. */}
                                {(activeForm === 'product' || activeForm === 'houseGoodsItem') && (
                                    <Product declaration={declaration} />
                                )}
                                {/* ICS2 only. Rendered directly rather than through
                                    HouseConsignmentForm, which needs a router outlet context and
                                    sets DeclarationContext.form to the kebab 'house-consignment'. */}
                                {activeForm === 'houseConsignment' && (
                                    <ICS2ENSHouseConsignmentForm formik={templateFormik} viewOnly={isViewOnly} />
                                )}
                            </DeclarationFormContent>
                        </StyledLayout>
                    </DeclarationContextProvider>
                </TemplateContext.Provider>
                <ValidationErrorContainer isOpen={errorsModalOpen} close={closeErrorsModal} />
            </FormikProvider>
        </>
    );
};

export default TemplateEdit;
